import { AttachmentFile } from '@nextforce/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useServer } from 'app/server/context'
import { appendFileToFormData } from 'chat/shared/util'
import { resolveMime } from 'friendly-mimes'
import keys from './shared/keys'

type Variables = {
  taskId: string
  exception_id?: string
  due_date?: string
  files?: AttachmentFile[]
}

export function useCompleteTask() {
  const { server } = useServer()
  const client = useQueryClient()

  return useMutation({
    mutationFn: completeTask,
    onSuccess: async () => {
      // Invalidate relevant queries
      await client.invalidateQueries({ queryKey: keys.all })
    },
  })

  async function completeTask({
    taskId,
    exception_id,
    due_date,
    files = [],
  }: Variables) {
    if (files.length > 4) {
      throw new Error('Maximum 4 files can be attached')
    }

    const url = `v2/tasks/custom/status/${taskId}`
    const formData = new FormData()

    // Add task completion data
    if (exception_id) {
      formData.append('exception_id', exception_id)
    }
    if (due_date) {
      formData.append('due_date', due_date)
    }

    // Handle file uploads if any
    if (files.length > 0) {
      // Convert files to FormData format
      for (let index = 0; index < files.length; index++) {
        try {
          const file = files[index]
          const fileExtension = resolveMime(file.mimeType).fileType
          const fileName = file.name || `file-${index}` + fileExtension
          const fileObj = {
            name: fileName,
            uri: file.uri,
            mimeType: file.mimeType,
          }
          await appendFileToFormData(formData, 'files', fileObj)
        } catch (error) {
          console.error(`Failed to process file ${index}: ${error.message}`)
        }
      }
    }

    const response = await server.post(
      url,
      formData,
      'user',
      'multipart/form-data'
    )
    return response
  }
}
